import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import OverlayLoading from '../../../../../Components/OverlayLoading';
import useNCZICommandProcess from '../../../Hooks/useNCZICommandProcess';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import Form from '../../../../../Components/Form/Form';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import withForm from '../../../../../Components/Form/withForm';
import FormControl from '../../../../../Components/Form/FormControl';
import TimePicker from '../../../../../Components/Base/TimePicker/TimePicker';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import moment, { Moment } from 'moment-timezone';
import { getDate } from '../../../../../Helpers/date.helper';

//@ts-ignore
import styles from './ElectronicVaccinationCreateReactionForm.module.scss';
import { NCZIExtension } from '../../../Interfaces/ncziTypes';
import InputWrapper from '../../../../../Components/Form/components/InputWrapper/InputWrapper';
import InputField from '../../../../../Components/Form/components/InputField';
import NCZIReferenceSelectField from '../../ReferenceSelect/NCZIReferenceSelectField';
import { NCZI_OID } from '../../../Enums/ncziOid.enum';
import FormContainer from '../../../../../Components/Form/FormContainer';
import NCZIReferenceSearchSelectField from '../../ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import FormTitle from '../../../../../Components/Form/FormTitle';
import {
	NCZIReferenceSelectItem,
	NCZIReferenceValidator,
} from '../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import { WritePatientVaccinationReaction } from '../../../Commands/WritePatientVaccinationReaction';

export interface ElectronicVaccinationCreateReactionFormValues {
	date: Moment;
	note: string;
	type: NCZIReferenceSelectItem;
	allergen: {
		drug: NCZIReferenceSelectItem;
		substance: NCZIReferenceSelectItem;
	};
	manifestation: {
		type: NCZIReferenceSelectItem;
		diagnosis: {
			code: NCZIReferenceSelectItem;
			specification: string;
		};
	};
}

interface Props {
	onClose?: (needReload: boolean) => void;
	patientIdentifier: string;
	vaccinationId: NCZIExtension;
}

const ElectronicVaccinationCreateReactionForm: React.FC<Props> = props => {
	const { onClose, patientIdentifier, vaccinationId } = props;
	const [defaultValues, setDefaultValues] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: () => onClose?.(true),
		});

	const handleSubmit = async (
		values: ElectronicVaccinationCreateReactionFormValues,
	) => {
		const finalValues = {
			createdAt: moment().format(),
			versionStatus: 'VER01',
			sensitivity: 3,
			uncertaintyExpressed: false,
			date: values.date.format('YYYY-MM-DD'),
			note: values.note || undefined,
			type: values.type.value,
			allergen: {
				drug: values.allergen.drug.value,
				substance: values.allergen.substance.value,
			},
			manifestation: {
				type: values.manifestation.type.value,
				diagnosis: {
					code: values.manifestation.diagnosis.code.value,
					specification:
						values.manifestation.diagnosis.specification || undefined,
				},
			},
			vaccinationId,
		};

		const writeCommand = WritePatientVaccinationReaction({
			patientCode: patientIdentifier,
			vaccinationReaction: finalValues,
		});

		await processCommand(writeCommand);
	};

	const validationSchema = yup.object({
		note: yup.string(),
		type: NCZIReferenceValidator('nczi.vaccination.reaction.create.type'),
		allergen: yup.object({
			drug: NCZIReferenceValidator(
				'nczi.vaccination.reaction.create.allergen.drug',
			),
			substance: NCZIReferenceValidator(
				'nczi.vaccination.reaction.create.allergen.substance',
			),
		}),
		manifestation: yup.object({
			type: NCZIReferenceValidator(
				'nczi.vaccination.reaction.create.manifestation.type',
			),
			diagnosis: yup.object({
				code: NCZIReferenceValidator(
					'nczi.vaccination.reaction.create.manifestation.diagnosis.code',
				),
				specification: yup.string(),
			}),
		}),
	});

	useEffect(() => {
		const now = getDate();
		setDefaultValues({
			date: now,
		});
		setIsLoading(false);
	}, []);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	return (
		<div>
			{isLoading && <OverlayLoading />}
			{!isLoading && (
				<Form defaultValues={defaultValues} schema={validationSchema}>
					<FormControl
						label='nczi.vaccination.reaction.create.date'
						name='date'
						isRequired>
						<DatePicker />
					</FormControl>
					<InputWrapper>
						<InputField
							label='nczi.vaccination.reaction.create.note'
							name='note'
							isRequired={false}
						/>
					</InputWrapper>
					<InputWrapper>
						<NCZIReferenceSelectField
							label='nczi.vaccination.reaction.create.type'
							name='type'
							oid={NCZI_OID.VACCINATION_REACTION_TYPE}
						/>
					</InputWrapper>
					<FormTitle
						tx='nczi.vaccination.reaction.create.allergen.title'
						type='default'
					/>
					<FormContainer name={'allergen'} settings={{ showLine: true }}>
						<InputWrapper>
							<NCZIReferenceSearchSelectField
								label='nczi.vaccination.reaction.create.allergen.drug'
								name='drug'
								oid={NCZI_OID.ATC_CLASSIFICATION_SYSTEM}
								isRequired
							/>
						</InputWrapper>
						<InputWrapper>
							<NCZIReferenceSelectField
								label='nczi.vaccination.reaction.create.allergen.substance'
								name='substance'
								oid={NCZI_OID.ALLERGENS}
								isRequired
							/>
						</InputWrapper>
					</FormContainer>
					<FormTitle
						tx='nczi.vaccination.reaction.create.manifestation.title'
						type='default'
					/>
					<FormContainer name={'manifestation'} settings={{ showLine: true }}>
						<InputWrapper>
							<NCZIReferenceSelectField
								label='nczi.vaccination.reaction.create.manifestation.type'
								name='type'
								oid={NCZI_OID.ADVERSE_REACTION_SYMPTOMS}
								isRequired
							/>
						</InputWrapper>
						<FormTitle
							tx='nczi.vaccination.reaction.create.manifestation.diagnosis.title'
							type='default'
						/>
						<FormContainer name={'diagnosis'} settings={{ showLine: true }}>
							<InputWrapper>
								<NCZIReferenceSearchSelectField
									label='nczi.vaccination.reaction.create.manifestation.diagnosis.code'
									name='code'
									oid={NCZI_OID.DIAGNOSIS}
									isRequired
								/>
							</InputWrapper>
							<InputWrapper>
								<InputField
									label='nczi.vaccination.reaction.create.manifestation.diagnosis.specification'
									name='specification'
								/>
							</InputWrapper>
						</FormContainer>
					</FormContainer>
					<div className='mt-4'>
						<SubmitButton
							title='component.nczi.setup.save'
							onSubmit={handleSubmit}
						/>
					</div>
				</Form>
			)}
		</div>
	);
};

export default withForm(ElectronicVaccinationCreateReactionForm);
